import { DashboardRule, OptimisationBase } from '@/optimizer';
import { withTimeout } from '@/optimizer/helpers/withTimeout';
import { useOptimizer } from '@/optimizer/hooks/useOptimizer';
import { differenceInDays } from 'date-fns';
import { useEffect, useRef, useState } from 'react';

const TOTAL_BUDGET = 'Total budget';

const OPTIMIZER_LOOKUP_TIMEOUT_MS = 10_000;

export type BudgetLimits = { min: number; max: number };
export type BudgetLimitsResult = { limits: BudgetLimits; loading: boolean };

type UseBudgetLimitsArgs = {
    disabledChannels: string[];
    startDate: Date;
    endDate: Date;
    rules: DashboardRule[];
    optimizationBase: OptimisationBase;
    enabled?: boolean;
    refreshToken?: string;
};

const EMPTY_LIMITS: BudgetLimits = { min: 0, max: 0 };

const durationDaysOf = (startDate: Date, endDate: Date): number =>
    Math.max(1, differenceInDays(endDate, startDate) + 1);

function useOptimizerLimits(
    startDate: Date,
    endDate: Date,
    optimizationBase: OptimisationBase,
    rules: DashboardRule[],
    disabledChannels: string[],
    enabled: boolean,
    refreshToken?: string,
): { limits: BudgetLimits; loading: boolean } {
    const { optimizer } = useOptimizer();
    const [limits, setLimits] = useState<BudgetLimits>(EMPTY_LIMITS);
    const [loading, setLoading] = useState(false);
    const requestIdRef = useRef(0);
    const startKey = startDate.getTime();
    const endKey = endDate.getTime();

    useEffect(() => {
        if (!enabled) {
            setLimits(EMPTY_LIMITS);
            setLoading(false);
            return;
        }

        setLimits(EMPTY_LIMITS);
        setLoading(true);

        const requestId = ++requestIdRef.current;
        const durationDays = durationDaysOf(startDate, endDate);

        let fetchLimits:
            | ((
                  startDateArg?: Date,
                  durationDays?: number,
                  options?: {
                      configRules?: DashboardRule[];
                      variables?: Record<string, number>;
                      scalePeriods?: boolean;
                  },
              ) => Promise<{ lower: number; upper: number }>)
            | undefined;

        if (optimizationBase === 'budget') {
            fetchLimits = optimizer.getBudgetLimits as typeof fetchLimits;
        } else if (optimizationBase === 'total_kpi') {
            fetchLimits = optimizer.getTotalKPILimits as typeof fetchLimits;
        } else {
            fetchLimits = optimizer.getKPILimits as typeof fetchLimits;
        }

        const budgetRules = rules.filter(
            (r) =>
                r.type === TOTAL_BUDGET &&
                (r.target === 'all' ||
                    r.target === 'campaign' ||
                    r.target === 'budget'),
        );
        const limitVariables = Object.fromEntries(
            disabledChannels.map((channel) => [channel, 0]),
        );

        (async () => {
            try {
                const limitsCall =
                    fetchLimits?.(startDate, durationDays, {
                        configRules: budgetRules,
                        variables: limitVariables,
                        scalePeriods: true,
                    }) ?? Promise.resolve(undefined);

                const range = await withTimeout(
                    limitsCall,
                    OPTIMIZER_LOOKUP_TIMEOUT_MS,
                    'Period-aware limits lookup',
                );

                if (requestId === requestIdRef.current && range) {
                    setLimits({
                        min: Math.round(range.lower),
                        max: Math.round(range.upper),
                    });
                }
            } catch (error) {
                console.error('Failed to fetch period-aware limits:', error);
                if (requestId === requestIdRef.current) {
                    setLimits(EMPTY_LIMITS);
                }
            } finally {
                if (requestId === requestIdRef.current) {
                    setLoading(false);
                }
            }
        })();
    }, [
        disabledChannels,
        enabled,
        optimizationBase,
        optimizer,
        refreshToken,
        rules,
        startKey,
        endKey,
    ]);

    return { limits, loading };
}

export const useBudgetLimits = ({
    disabledChannels,
    startDate,
    endDate,
    rules,
    optimizationBase,
    enabled = true,
    refreshToken,
}: UseBudgetLimitsArgs): BudgetLimitsResult => {
    return useOptimizerLimits(
        startDate,
        endDate,
        optimizationBase,
        rules,
        disabledChannels,
        enabled,
        refreshToken,
    );
};
